/**
 * ScandiPWA - Progressive Web App for Magento
 *
 * Copyright © Scandiweb, Inc. All rights reserved.
 * See LICENSE for license details.
 *
 * @license OSL-3.0 (Open Software License ("OSL") v. 3.0)
 * @package scandipwa/base-theme
 * @link https://github.com/scandipwa/base-theme
 */

import PropTypes from 'prop-types';
import { Fragment, PureComponent } from 'react';

import ExpandableContent from 'Component/ExpandableContent';
import ProductAttributeValue from 'Component/ProductAttributeValue';
import { AttributeType } from 'Type/ProductList';
import {
    PRODUCT_ATTRIBUTE_COMPONENTS,
    PRODUCT_ATTRIBUTE_INSTRUCTIONS,
    PRODUCT_ATTRIBUTES_EXCLUDE
} from 'Route/ProductPage/ProductPage.config';
import history from 'Util/History';

import { ProductAttributes as SourceProductAttributes } from 'SourceComponent/ProductAttributes/ProductAttributes.component';
/** @namespace Component/ProductAttributes/Component */
export class ProductAttributes extends SourceProductAttributes {
    static propTypes = {
        areDetailsLoaded: PropTypes.bool.isRequired,
        attributesWithValues: AttributeType.isRequired
    };

    renderGroups() {
        const { attributesWithValues } = this.props;

        const groups = Object.values(attributesWithValues).map(
            (attribute) => ({
                attribute_group_id: attribute.attribute_group_id,
                attribute_group_name: attribute.attribute_group_name,
                attribute_group_code: attribute.attribute_group_code
            })
        );

        const uniqueGroups = groups.filter(
            (group, index, array) => (
                index === array.findIndex((g) => g.attribute_group_id === group.attribute_group_id)
            )
        );

        return (
            uniqueGroups.map(
                (group) => (
                    this.renderAttributes(group.attribute_group_id)
                )
            )
        );
    }

    renderAttributeBlock(attribute) {
        if(attribute?.attribute_code && PRODUCT_ATTRIBUTES_EXCLUDE.includes(attribute.attribute_code) || !Object.values(attribute?.attribute_options).length > 0)
            return null
        
        if (attribute?.attribute_type === 'select' && !Object.keys(attribute.attribute_options).length ||
            !Object.keys(attribute.attribute_options).length > 0 && attribute?.attribute_value < 3) {
            return null
        }
        return (
            <div block="ProductAttributes" elem="Attribute" key={ attribute.attribute_label } className={ attribute && attribute.attribute_value && attribute.attribute_options && attribute.attribute_options[attribute.attribute_value] ? attribute.attribute_options[attribute.attribute_value].label : "" }>
                <div block="ProductAttributes" elem="AttributeLabel">
                    { attribute.attribute_label }
                </div>
                <div block="ProductAttributes" elem="ValueLabel">
                    <ProductAttributeValue
                        key={ attribute.attribute_label }
                        attribute={ attribute }
                        isFormattedAsText
                    />
                </div>
            </div>
        );
    };

    renderAttributes(attribute_group_id) {
        const { attributesWithValues } = this.props;

        if (!Object.keys(attributesWithValues).length) {
            return null;
        }

        const filteredAttributesWithValues = Object.values(attributesWithValues).filter(
            (attribute) => attribute.attribute_group_id === attribute_group_id
        );

        if (!filteredAttributesWithValues.length) {
            return null;
        }
        return (
            <div block="ProductAttributes" elem="Attributes">
                { filteredAttributesWithValues.map((item) => (
                    this.renderAttributeBlock(item)
                )) }
            </div>
        );
    }

    renderSelectedConfigurableBarcodeAttribute() {
        const {
            location: {
                search
            }
        } = history;
        const {
            productOrVariant : {
                attributes
            } = {},
            product : {
                type_id,
            } = {},
            isProductInformation
        } = this.props;
        
        if(search && type_id === 'configurable' && !isProductInformation) {
            let barcodeVal = attributes?.barcode?.attribute_value;
            if(barcodeVal){
                return (
                    <div block="ProductAttributes" elem="Attributes">
                        <div block="ProductAttributes" elem="Attribute">
                            <div block="ProductAttributes" elem="AttributeLabel">{ attributes.barcode.attribute_label }</div>
                            <div block="ProductAttributes" elem="ValueLabel">{ barcodeVal }</div>
                        </div>
                    </div>
                );
            }

            return null;
        }

        return null;
    }

    renderContent() {
        const { areDetailsLoaded } = this.props;
        const heading = areDetailsLoaded ? __('Details') : '';
        
        return (
            <div mix={ { block: 'ProductAttributes', elem: 'Content' } }>
                { this.renderGroups() }
                { this.renderSelectedConfigurableBarcodeAttribute() }
            </div>
        );
    }
}

export default ProductAttributes;
